<script setup lang="ts">
import type {
  Event,
  EventPreference,
  EventsOverview,
  EventsOverviewProduct,
  Product,
} from "mws-ts-v2-sdk";

import type { TailwindBreakpoint } from "@/utils/styleUtils";
import type BaseMediaGallery from "~/components/base/display/BaseMediaGallery.vue";

const MAX_SLIDER_PRODUCTS = 50;

type Props = {
  products: Product[] | EventsOverviewProduct[];
  event?: EventsOverview | Event | EventPreference;
  eventListIndex?: number;
  trackingSource: string;
};

const props = defineProps<Props>();

type Emits = {
  (e: "product-clicked", product: Product | EventsOverviewProduct): void;
  (e: "product-finished"): void;
};

defineEmits<Emits>();

const localePath = useLocalePath();

const productSlider = ref<InstanceType<typeof BaseMediaGallery> | null>();
const currentSlide = computed(() => {
  const currentSlideIndex = productSlider.value?.currentSlideIndex ?? 0;

  return currentSlideIndex === Infinity ? 0 : currentSlideIndex;
});

const { currentBreakpoint } = useScreenSize();

/**
 * The number of cards shown per page for each tailwind breakpoint.
 * It is related to the width defined in the `.product-card-container__product-card` class
 */
const numberOfCardsPerPage: Record<TailwindBreakpoint, number> = {
  xs: 1.5,
  sm: 1.5,
  md: 3,
  lg: 4,
  xl: 4,
  "2xl": 4,
  "3xl": 5,
  "4xl": 5,
  "5xl": 5,
  "6xl": 5,
  "7xl": 5,
};

const mediaGalleryPageSize = computed(() => {
  return numberOfCardsPerPage[currentBreakpoint.value];
});

// Only render cards that are within the range of the current slide index.
const cardIndexesToRender = computed(() => {
  const firstCardIndexShowing = Math.floor(currentSlide.value);
  // Grab the largest page size to ensure we always render enough cards.
  const largestPageSize = numberOfCardsPerPage["7xl"];

  return {
    from: firstCardIndexShowing - largestPageSize,
    to: firstCardIndexShowing + 2 + largestPageSize,
  };
});

const { viewAllProductsClicked } = useAnalytics();
const onViewAllProductsClicked = () => {
  viewAllProductsClicked({ event_name: props.event?.name });
};
</script>

<template>
  <div
    class="product-card-container"
    data-cy="product-card-container"
  >
    <BaseMediaGallery
      v-if="products.length > 0"
      ref="productSlider"
      :page-size="mediaGalleryPageSize"
      class="product-card-container__container"
    >
      <li
        v-for="(product, index) in products"
        :key="product.id"
        class="product-card-container__product-card"
      >
        <template
          v-if="
            index >= cardIndexesToRender.from && index <= cardIndexesToRender.to
          "
        >
          <ProductCard
            :product="product"
            :event="event"
            :enable-image-lazy-loading="index > currentSlide + 5"
            :event-list-index="eventListIndex"
            :tracking-source="trackingSource"
            data-cy="product-card"
            data-test="product-card"
            @product-clicked="$emit('product-clicked', product)"
            @product-finished="$emit('product-finished')"
          />
        </template>
        <div
          v-else
          class="product-card-container__product-card-placeholder"
        />
      </li>

      <li
        v-if="products.length === MAX_SLIDER_PRODUCTS && event"
        class="product-card-container__product-card bg-platform-bg-2"
      >
        <NuxtLink
          :to="localePath(getEventRoute(event))"
          class="flex h-full"
          @click="onViewAllProductsClicked()"
        >
          <span class="m-auto uppercase font-bold">
            {{ $t("paginator.viewAllProducts") }}
          </span>
        </NuxtLink>
      </li>
    </BaseMediaGallery>
    <BaseSliderButtonBar
      :slider="productSlider"
      class="absolute top-[-4.75rem] right-0 h-16 hidden md:flex"
    >
      <template
        v-if="event"
        #action
      >
        <EventFollowButton
          :id="event.id"
          :name="event.name ?? ''"
          :slug="event.slug"
          :is-followed-by-user="!!event.isFollowedByUser"
        />
      </template>
    </BaseSliderButtonBar>
  </div>
</template>

<style lang="postcss">
.product-card-container {
  @apply relative;

  &__container {
    @apply overflow-x-scroll flex min-w-full;

    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    scroll-snap-type: x mandatory;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__product-card {
    /** the width is based on the `numberOfCardsPerPage` */
    /** Make sure we always load enough images through lazy loading, and take the gap into account */
    @apply w-2/3 min-w-[calc(66.66%-.17rem)];
    @apply md:w-1/3 md:min-w-[calc(33.33%-.17rem)];
    @apply lg:w-1/4 lg:min-w-[calc(25%-0.18rem)];
    @apply 3xl:w-1/5 3xl:min-w-[calc(20%-.23rem)];
    @apply scroll-ml-4 md:scroll-ml-0;

    &:last-child {
      @apply scroll-mr-4 mr-4 md:scroll-mr-0 md:mr-0;
    }
  }

  &__product-card-placeholder {
    @apply w-full aspect-[4/5];
  }
}
</style>
